import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PURCHASE_INTENT_PARAM, type JourneyRoute, type PurchaseIntent } from '@/lib/constants/issue-report';
import { clearPurchaseIntent, parsePurchaseIntent, storePurchaseIntent } from '@/lib/auth/intent-storage';

/**
 * The intent named by `?intent=` on the current URL, or null. Anything but
 * `issue-report` is ignored. Never reads storage, so a stored intent from an
 * earlier visit cannot count as "this visitor came from the Issue Report
 * button" (FR-207: the company-email rule belongs to that signup only).
 */
export function useUrlPurchaseIntent(): PurchaseIntent | null {
  const [searchParams] = useSearchParams();
  return parsePurchaseIntent(searchParams.get(PURCHASE_INTENT_PARAM));
}

/**
 * The Issue Report purchase intent for a login or signup page (FR-206): taken
 * from `?intent=` when it names the known value and stored for 7 days. With no
 * `?intent=` the page is a plain login or signup ("Log In", "Sign Up Free"), so
 * any intent left from an earlier visit is forgotten (FR-219): only the Start
 * My Report button begins the journey. Links inside the journey carry the
 * parameter (`journeySignInPath`), so they keep it.
 *
 * @param route - the page's own route, stored with the intent so the progress
 *   bar shows that route's steps on every later screen (FR-224)
 * @returns the intent in force, or null for a normal login or signup
 */
export function usePurchaseIntent(route: JourneyRoute): PurchaseIntent | null {
  const fromUrl = useUrlPurchaseIntent();

  useEffect(() => {
    if (fromUrl) storePurchaseIntent(fromUrl, Date.now(), route);
    else clearPurchaseIntent();
  }, [fromUrl, route]);

  return fromUrl;
}
